/**
 * AnswerRenderer — one walk over an Answer Document's blocks.
 *
 * The server composed the document from typed datasets; nothing here guesses.
 * A column's unit is what the schema said, an id is an entity because the
 * column declared one (or the document's entity list knows the value), a
 * table paginates above its page size and charts never do. Text blocks are
 * the only markdown.
 */
import { lazy, memo, Suspense, useMemo, useState } from "react";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import ChatInlineChart from "shared/components/chat/ChatInlineChart";
import AnswerMarkdown from "shared/components/chat/AnswerMarkdown";
import { EntityLink } from "shared/components/chat/ChatRichBlocks";
import ActionCard from "shared/components/chat/ActionCard";
import ChatNotice from 'shared/components/chat/ChatNotice';
import RecordsBlock from 'shared/components/records/RecordsBlock';
import EvalCreatedCard from 'shared/components/chat/EvalCreatedCard';
import ProposalCard from 'shared/components/chat/ProposalCard';
import { formatValue, formatDurationMs, percentOf, valueText, NUMERIC_STRING_RE } from "shared/components/chat/chatCellFormat";
import { parseAnswerDocument } from "shared/types/answerDocument";
import useAnswerReveal, { pieceCost, partialText, TEXT_KINDS } from "shared/components/chat/useAnswerReveal";
import { openInChat } from "shared/components/records/recordsSurface";
import ChatPager, { usePaged, BLOCK_TITLE_STYLE } from "shared/components/chat/ChatPager";
import { OPENABLE_KINDS } from "shared/components/chat/ChatMarkdownLink";
import AddToDashboard from "features/dashboards/components/AddToDashboard";
import {
  tableHeaderRowClass,
  tableHeaderCellClass,
  tableRowClass,
  tableDataCellClass,
} from "features/activity/styles/dataTableStyles";

// The report card draws dashboard cards (and their rich-text editor): loaded when a report is in the answer.
const ReportCard = lazy(() => import('shared/components/chat/ReportCard'));

// A row with nothing to open: the table row, without the pointer and hover.
const STATIC_ROW_CLASS = "h-8 border-b border-[color:var(--border-subtle)] last:border-b-0";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::\d{2})?(?:\.\d+)?Z?)?$/;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const cardBase = {
  border: `1px solid ${theme.border}`,
  borderRadius: 12,
  backgroundColor: "transparent",
};

/** Chart kinds the inline chart draws; the composer's richer names map onto them. */
const CHART_KIND_MAP = {
  line: "line", area: "area", stacked_area: "area", stacked_line: "area", percent_area: "area",
  bar: "bar", stacked_bar: "bar", percent_bar: "bar", grouped_bar: "bar", hbar: "bar", stacked_hbar: "bar", histogram: "bar",
  pie: "pie", donut: "donut", scatter: "scatter", heatmap: "heatmap", funnel: "funnel",
  sankey: "sankey", matrix: "matrix", retention_matrix: "retention_matrix",
  treemap: "treemap", radar: "radar", combo: "combo",
};

/** How an ADM kind is drawn beyond its base shape: stacking, 100%, sideways, lines only. */
const CHART_STYLE = {
  stacked_area: { stacked: true },
  stacked_line: { stacked: true, lineOnly: true },
  percent_area: { stacked: true, percent: true },
  stacked_bar: { stacked: true },
  percent_bar: { stacked: true, percent: true },
  hbar: { horizontal: true },
  stacked_hbar: { stacked: true, horizontal: true },
};

/** An ADM chart spec → the {xKey, series, data} shape ChatInlineChart renders. */
export function chartSpecFromBlock(block) {
  const spec = block && block.spec;
  if (!spec || !spec.dimension || !Array.isArray(spec.measures) || !Array.isArray(spec.data)) return null;
  return {
    version: 1,
    kind: CHART_KIND_MAP[spec.kind] || "bar",
    ...(CHART_STYLE[spec.kind] || {}),
    title: spec.title || "",
    xKey: spec.dimension.key,
    ...(spec.pair ? { yKey: spec.pair.key, xLabel: spec.dimension.label, yLabel: spec.pair.label } : {}),
    ...(spec.kind === "scatter" ? { xLabel: spec.dimension.label, xUnit: spec.dimension.unit ?? null } : {}),
    series: spec.measures.map((m) => ({ name: m.label || m.key, dataKey: m.key })),
    data: spec.data,
    ...(spec.unit ? { unit: spec.unit } : {}),
  };
}

/**
 * A column name as a person reads it.
 *
 * Labels default to the column KEY, which is a SQL alias — so a KPI tile read
 * "P50_DURATION" and a table header "FAILURE_RATE". Underscores are how the
 * database spells a space; nothing else is changed, because the rest of the
 * name is the analyst's own wording and not ours to rewrite.
 */
export function humanLabel(label) {
  return String(label ?? "").replace(/_+/g, " ").trim();
}

export function formatTime(v) {
  const m = ISO_RE.exec(String(v ?? "").trim());
  if (!m) return String(v ?? "");
  const [, y, mo, d, h, mi] = m;
  const day = `${MONTHS[Number(mo) - 1]} ${Number(d)}, ${y}`;
  if (!h || (h === "00" && mi === "00")) return day;
  return `${day} ${h}:${mi}`;
}

/**
 * Money, at a precision a person would write. Must match the server's
 * `money()` in services/ask/answer/figures.js — the prose and the tile show
 * the same figure and cannot be allowed to round it differently.
 *
 * A flat toFixed(4) is right for the fractions this product deals in (a run
 * costing $0.0874) and wrong for every total above a dollar: live tiles read
 * "$48.6787".
 */
export function money(v) {
  if (!Number.isFinite(v)) return "0";
  // The bug was PRECISION ON TOTALS, not the product's trailing-zero style:
  // live tiles read "$48.6787". Sub-dollar amounts keep four places because
  // that is where this product's signal is — a run costs $0.0874, and $0.09 is
  // a different claim.
  // Under a cent, three significant digits, as the server does: $0.000516
  // and $0.000512 both read "$0.0005" at four places.
  // From a dollar up, cents and all ("$4.70"), as the server prints it.
  const a = Math.abs(v);
  if (a >= 1) return v.toFixed(2);
  const dp = a >= 0.01 || a === 0 ? 4 : Math.min(10, 2 - Math.floor(Math.log10(a)));
  // At least two places ($0.50); more only where the figure needs them.
  return a === 0 ? "0" : v.toFixed(dp).replace(/(\.\d{2}\d*?)0+$/, "$1");
}

/** A cell, formatted by the column's declared unit and role — nothing inferred from its name. */
export function formatCell(v, col) {
  if (v == null || v === "") return "";
  const unit = col && col.unit;
  const role = col && col.role;
  if (role === "time") return formatTime(v);
  // Postgres sends NUMERIC (every AVG) as a string: a measure printed it raw.
  if (typeof v === "string" && (unit || role === "measure") && NUMERIC_STRING_RE.test(v.trim())) v = Number(v);
  if (typeof v === "number") {
    if (unit === "percent") return `${(col && col.points ? v : percentOf(v, col && col.key)).toFixed(1)}%`;
    if (unit === "currency") return `$${money(v)}`;
    if (unit === "duration_ms") return formatDurationMs(v);
    return formatValue(v);
  }
  return valueText(v);
}

/** The entity kind a cell links as: the column's declaration first, then the document's entity list. */
function entityKindFor(value, col, entityIndex) {
  if (col && col.entity) return col.entity;
  const s = String(value ?? "");
  if (!s) return null;
  if (UUID_RE.test(s)) return entityIndex.get(s.toLowerCase()) || null;
  if (EMAIL_RE.test(s)) return entityIndex.get(s) || "user";
  return null;
}

/* ── headline ─────────────────────────────────────────────────────────── */

function Headline({ headline }) {
  if (!headline || !headline.text) return null;
  return (
    <div className="trodo-answer-headline" style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.45, marginBottom: 12 }}>
      <AnswerMarkdown text={headline.text} />
    </div>
  );
}

/* ── kpis ─────────────────────────────────────────────────────────────── */

export const KpiStrip = memo(function KpiStrip({ block }) {
  if (!block || !Array.isArray(block.items) || !block.items.length) return null;
  return (
    <div className="trodo-answer-block trodo-answer-kpis my-3">
      {block.title && (
        <div style={{ ...BLOCK_TITLE_STYLE, marginBottom: 6 }}>{block.title}</div>
      )}
      <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
        {block.items.map((k, i) => (
          <div key={`kpi-${i}`} style={{ ...cardBase, padding: "10px 14px" }}>
            <div
              title={humanLabel(k.label)}
              style={{
                fontSize: 11, fontWeight: 500, color: theme.textSecondary, textTransform: "uppercase",
                letterSpacing: "0.04em", marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}
            >
              {humanLabel(k.label)}
            </div>
            <div style={{ fontSize: 22, fontWeight: 600, color: theme.textPrimary, fontVariantNumeric: "tabular-nums" }}>
              {k.display ?? formatCell(k.value, { unit: k.unit })}
            </div>
            {k.delta && <div style={{ fontSize: 11, marginTop: 2, color: theme.textSecondary }}>{k.delta}</div>}
          </div>
        ))}
      </div>
    </div>
  );
});

/* ── chart ────────────────────────────────────────────────────────────── */

export const ChartBlock = memo(function ChartBlock({ block }) {
  const spec = useMemo(() => chartSpecFromBlock(block), [block]);
  if (!spec || !spec.data.length) return null;
  return (
    <div className="trodo-answer-block trodo-answer-chart my-3 [&>.chat-inline-chart]:mt-0">
      <ChatInlineChart spec={spec} />
    </div>
  );
});

/* ── table ────────────────────────────────────────────────────────────── */

function sortRows(rows, sort) {
  if (!sort) return rows;
  const { key, dir } = sort;
  return [...rows].sort((a, b) => {
    const av = a[key]; const bv = b[key];
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === "number" && typeof bv === "number") return dir * (av - bv);
    return dir * String(av).localeCompare(String(bv));
  });
}

export const AnswerTable = memo(function AnswerTable({ block, entityIndex }) {
  const [sort, setSort] = useState(null);
  const columns = (block && block.columns) || [];
  const rowsIn = block && block.rows;
  const allRows = useMemo(() => rowsIn || [], [rowsIn]);
  const sorted = useMemo(() => sortRows(allRows, sort), [allRows, sort]);
  // Every chat table pages at the same size, whatever page_size an older answer stored.
  const paged = usePaged(sorted);
  const { visible, page: current } = paged;
  const index = entityIndex || new Map();
  // A column states its percents on one scale: any value above 1 means they
  // are all points, so 0.509 beside 2.58 reads 0.5% and 2.6%, not 50.9%.
  const cellCols = useMemo(() => columns.map((c) => (
    c.unit === "percent" && allRows.some((r) => Math.abs(Number(r[c.key])) > 1) ? { ...c, points: true } : c
  )), [columns, allRows]);
  if (!columns.length) return null;

  const onSort = (key) => {
    setSort((s) => (s?.key === key ? (s.dir === -1 ? null : { key, dir: -1 }) : { key, dir: 1 }));
  };

  return (
    <div className="trodo-answer-block trodo-answer-table my-3">
      {block.title && (
        <div style={{ ...BLOCK_TITLE_STYLE, marginBottom: 6 }}>{block.title}</div>
      )}
      <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
        <div className="overflow-x-auto">
          <table className="text-left" style={{ width: "max-content", minWidth: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr className={tableHeaderRowClass}>
                {columns.map((c) => {
                  const active = sort?.key === c.key;
                  return (
                    <th
                      key={c.key}
                      onClick={() => onSort(c.key)}
                      title="Sort"
                      className={`${tableHeaderCellClass} cursor-pointer select-none ${c.role === "measure" ? "!text-right" : ""} ${active ? "!text-[color:var(--text-primary)]" : ""}`}
                    >
                      {humanLabel(c.label || c.key)}{active ? (sort.dir === 1 ? " ↑" : " ↓") : ""}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visible.map((r, ri) => {
                const clickable = columns.some((c) => { const raw = r[c.key]; return raw != null && String(raw).trim() && entityKindFor(raw, c, index); });
                return (
                  <tr
                    key={`r-${current}-${ri}`}
                    // THE WHOLE ROW OPENS THE RECORD, not just the id cell (Akshit,
                    // 3 Sep). A click anywhere on a row that carries an entity link
                    // is a click on that link; the chat page already handles it.
                    onClick={(e) => {
                      if (e.target.closest && e.target.closest('a')) return;
                      const a = e.currentTarget.querySelector('a[href^="trodo://entity/"]');
                      if (a) a.click();
                    }}
                    className={clickable ? tableRowClass : STATIC_ROW_CLASS}
                  >
                    {cellCols.map((c) => {
                      const raw = r[c.key];
                      const display = formatCell(raw, c);
                      const kind = raw != null && String(raw).trim() ? entityKindFor(raw, c, index) : null;
                      return (
                        <td
                          key={`c-${ri}-${c.key}`}
                          className={`${tableDataCellClass} tabular-nums whitespace-nowrap ${c.role === "measure" ? "!text-right" : ""}`}
                        >
                          {kind ? <EntityLink kind={kind} id={String(raw)}>{c.role === "id" && UUID_RE.test(String(raw)) ? String(raw).slice(0, 8) : display}</EntityLink> : display}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <ChatPager
        paged={paged}
        note={block.rows_total && block.rows_total > allRows.length ? ` shown · ${block.rows_total.toLocaleString()} in total` : ""}
      />
    </div>
  );
});

/* ── findings / caveat / text ─────────────────────────────────────────── */

function FindingsBlock({ block }) {
  if (!block || !Array.isArray(block.items) || !block.items.length) return null;
  return (
    <ul className="trodo-answer-block trodo-answer-findings my-3" style={{ listStyleType: "disc", paddingLeft: 22 }}>
      {block.items.map((f, i) => (
        <li key={`f-${i}`} style={{ margin: "4px 0" }}>
          <AnswerMarkdown text={f.text || ""} />
          {/* The deep engine writes high | medium | low; older answers carry 0–1. */}
          {(typeof f.confidence === "number" || ["high", "medium", "low"].includes(f.confidence)) && (
            <span style={{ fontSize: 11, color: theme.textSecondary, marginLeft: 6 }}>{typeof f.confidence === "number" ? `${Math.round(f.confidence * 100)}%` : f.confidence} confidence</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function CaveatBlock({ block }) {
  if (!block || !block.text) return null;
  // Prose, like every other prose block. Rendered as a raw string, a caveat
  // carrying citations showed the reader the markup itself —
  // `[5](trodo://cite/5 "…")` — so the one block whose job is to warn them
  // arrived in the one form they cannot read.
  return (
    <ChatNotice tone="warning" className="trodo-answer-block trodo-answer-caveat my-3">
      <AnswerMarkdown text={block.text} />
    </ChatNotice>
  );
}

/* ── the document ─────────────────────────────────────────────────────── */

function ActionsBlock({ block }) {
  if (!block || !Array.isArray(block.items) || !block.items.length) return null;
  return (
    <div className="trodo-answer-block trodo-answer-actions my-3" style={{ display: "grid", gap: 8 }}>
      {block.items.map((a, i) => <ActionCard key={a.proposal_id || `a-${i}`} item={a} />)}
    </div>
  );
}

const BLOCK_RENDERERS = {
  kpis: (b, ctx) => <KpiStrip block={b} />,
  chart: (b, ctx) => <ChartBlock block={b} />,
  table: (b, ctx) => <AnswerTable block={b} entityIndex={ctx.entityIndex} />,
  text: (b) => <div className="trodo-answer-block trodo-answer-text"><AnswerMarkdown text={b.markdown || ""} /></div>,
  findings: (b) => <FindingsBlock block={b} />,
  caveat: (b) => <CaveatBlock block={b} />,
  actions: (b) => <ActionsBlock block={b} />,
  // The product's own records, in the page's own table (lookup path, show_records).
  records: (b) => <div className="trodo-answer-block my-3"><RecordsBlock block={b} /></div>,
  // An eval Ask created: the card with its steps, the sample, Enable.
  eval: (b) => <EvalCreatedCard block={b} />,
  // A change Ask proposed to an eval: why, what it fixes, Put in production.
  proposal: (b) => <ProposalCard block={b} />,
  // A report Ask built or changed: the cards it added, drawn live, and Open report.
  report: (b) => <Suspense fallback={null}><ReportCard block={b} /></Suspense>,
};

/* ── sources (deep) ───────────────────────────────────────────────────── */

const SOURCE_LABEL_RE = /^(?:measured|observed|judged)\s*[:—-]\s*/i;

/**
 * What a deep report's numbered citations rest on, at the foot of the report.
 * A citation used to be a superscript with the claim on hover and nothing on
 * click unless the claim named a run; a reader on a phone, where nothing
 * hovers, could not read a single source. Clicking a number now opens this
 * list at that source.
 */
/** A source's claim without its "measured:" label, starting as a sentence does. */
function sourceText(claim) {
  const t = String(claim || "").replace(SOURCE_LABEL_RE, "").trimStart();
  // "this month, …" becomes "This month, …"; a name ("support-agent") stays as written.
  return /^[a-z]+(?![\w.-])/.test(t) ? t.charAt(0).toUpperCase() + t.slice(1) : t;
}

function SourcesList({ sources }) {
  if (!Array.isArray(sources) || !sources.length) return null;
  return (
    <details className="trodo-answer-block trodo-answer-sources my-3" style={{ fontSize: 13, color: theme.textSecondary }}>
      <summary style={{ ...BLOCK_TITLE_STYLE, cursor: "pointer" }}>Sources ({sources.length})</summary>
      <ol style={{ listStyle: "none", padding: 0, margin: "8px 0 0", display: "grid", gap: 6 }}>
        {sources.map((src) => (
          <li key={src.n} data-source={src.n} className="trodo-answer-source" style={{ display: "flex", gap: 8, alignItems: "baseline", borderRadius: 6, padding: "2px 4px" }}>
            <span style={{ minWidth: 18, fontSize: 11, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{src.n}</span>
            <span style={{ minWidth: 0, flex: 1 }}>
              {src.kind && src.kind !== "measured" && <span style={{ fontSize: 11, marginRight: 6 }}>{src.kind}</span>}
              <span style={{ color: theme.textPrimary }}><AnswerMarkdown text={sourceText(src.claim)} /></span>
              {src.entity && src.entity.id && OPENABLE_KINDS.has(src.entity.kind) && (
                <button type="button" onClick={() => openInChat(src.entity.kind, src.entity.id)}
                  style={{ marginTop: 2, fontSize: 12, color: "rgb(var(--trodo-primary-rgb))", background: "none", border: "none", padding: 0, cursor: "pointer" }}>
                  Open {src.entity.kind}
                </button>
              )}
            </span>
          </li>
        ))}
      </ol>
    </details>
  );
}

/**
 * @param {object} props
 * @param {boolean} [props.reveal]  a live answer: type it out (useAnswerReveal)
 */
export default function AnswerRenderer({ answer, messageId = null, reveal = false }) {
  const doc = useMemo(() => parseAnswerDocument(answer), [answer]);
  // Headline, then every block, in the order they are read.
  const pieces = useMemo(() => (doc ? [
    ...(doc.headline && doc.headline.text ? [{ kind: "headline", text: doc.headline.text }] : []),
    ...(doc.blocks || []).filter((b) => b && BLOCK_RENDERERS[b.kind]).map((b) => ({ kind: b.kind, text: b.markdown })),
  ] : []), [doc]);
  const spent = useAnswerReveal(reveal, pieces);
  const entityIndex = useMemo(() => {
    const m = new Map();
    for (const e of (doc && doc.entities) || []) {
      if (e && e.id && e.kind) m.set(e.kind === "user" ? String(e.id) : String(e.id).toLowerCase(), e.kind);
    }
    return m;
  }, [doc]);
  if (!doc) return null;
  const ctx = { entityIndex };
  // While revealing: how much of each piece the budget has reached.
  let offset = 0;
  const reach = (kind, text) => {
    const cost = pieceCost(kind, text);
    const from = offset;
    offset += cost;
    if (spent == null || spent >= from + cost) return { whole: true };
    if (spent <= from) return { hidden: true };
    return TEXT_KINDS.has(kind) || kind === "headline" ? { chars: spent - from } : { hidden: true };
  };
  const head = doc.headline && doc.headline.text ? reach("headline", doc.headline.text) : { whole: true };
  return (
    <div className="trodo-answer" data-engine={doc.meta && doc.meta.engine} aria-busy={spent != null || undefined}>
      {!head.hidden && <Headline headline={head.chars != null ? { ...doc.headline, text: partialText(doc.headline.text, head.chars) } : doc.headline} />}
      {(doc.blocks || []).map((b, i) => {
        const render = b && BLOCK_RENDERERS[b.kind];
        if (!render) return null;
        const at = reach(b.kind, b.markdown);
        if (at.hidden) return null;
        if (at.chars != null) {
          return <div key={`b-${i}-${b.kind}`}>{render({ ...b, markdown: partialText(b.markdown, at.chars) }, ctx)}</div>;
        }
        // A chart that knows what produced it can become a live dashboard card.
        const pinnable = messageId && b.provenance && ["chart", "table", "kpis"].includes(b.kind);
        return (
          <div key={`b-${i}-${b.kind}`} className={spent != null && !TEXT_KINDS.has(b.kind) ? "trodo-answer-in" : undefined}>
            {render(b, ctx)}
            {pinnable ? <AddToDashboard messageId={messageId} blockIndex={i} title={(b.spec && b.spec.title) || b.title || null} /> : null}
          </div>
        );
      })}
      {spent == null && <SourcesList sources={doc.meta && doc.meta.sources} />}
      {spent != null && <style>{"@keyframes trodo-answer-in{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}.trodo-answer-in{animation:trodo-answer-in .28s ease-out}"}</style>}
    </div>
  );
}
